import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import "../../../i18n";
import BrowserFrame from "../components/BrowserFrame";
import usePlatformStats from "../../../hooks/usePlatformStats";

const HeroReportPreview = lazy(() => import("../components/HeroReportPreview"));

// Exact figures on purpose — "13,399" reads as a real count where "13,000+"
// reads as marketing (owner, 2026-09-08). en-IN grouping for all locales.
const fmt = (n) => n.toLocaleString("en-IN");

/**
 * "8 Sep 2026" from an ISO date, for the census caption. Falls back to the raw
 * string rather than printing "Invalid Date" at a visitor.
 */
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

function HeroSection() {
  const { t } = useTranslation("marketing");
  const stats = usePlatformStats();
  const censusDate = fmtDate(stats.asOf);

  return (
    <div className="ul-hero">
      <div className="ul-hero-left">
        <div className="ul-hero-badge">
          <span className="dot" aria-hidden="true" />
          {t("hero.badge")}
        </div>
        <h1>
          {t("hero.title1")}
          <span className="highlight">{t("hero.title2")}</span>
        </h1>
        <p className="ul-hero-sub">
          <Trans t={t} i18nKey="hero.sub" />
        </p>
        <div className="ul-hero-ctas">
          <Link to="/request-demo" className="ul-btn-hero-primary">
            {t("hero.ctaPrimary")}
          </Link>
          <Link to="/teacher-login" className="ul-btn-hero-secondary">
            {t("hero.ctaSecondary")}
          </Link>
        </div>
        <div className="ul-hero-cta-note">
          {t("hero.ctaNote")}
        </div>

        <div className="ul-hero-trust">
          <div className="ul-hero-trust-group" aria-label={t("hero.builtFor")}>
            <span className="ul-hero-audience-label">{t("hero.builtFor")}</span>
            <span className="ul-hero-chip" style={{ background: "#FFF7ED", color: "#C2410C" }}>{t("hero.chips.teachers")}</span>
            <span className="ul-hero-chip" style={{ background: "#EFF6FF", color: "#2563EB" }}>{t("hero.chips.schools")}</span>
            <span className="ul-hero-chip" style={{ background: "#F0FDF4", color: "#16A34A" }}>{t("hero.chips.coaching")}</span>
            <span className="ul-hero-chip" style={{ background: "#FAF5FF", color: "#8B5CF6" }}>{t("hero.chips.tuition")}</span>
          </div>
          <div className="ul-hero-trust-group" aria-label={t("hero.boardsAria")}>
            <span className="ul-hero-audience-label">{t("hero.boardsLabel")}</span>
            <span className="ul-hero-chip ul-chip-board"><span aria-hidden="true">✓</span>CBSE</span>
            <span className="ul-hero-chip ul-chip-board"><span aria-hidden="true">✓</span>ICSE</span>
            <span className="ul-hero-chip ul-chip-board"><span aria-hidden="true">✓</span>{t("hero.chips.stateBoards")}</span>
          </div>
        </div>

        <div className="ul-hero-stats">
          {/* Real platform numbers. First paint = baked census
              (usePlatformStats.BAKED_STATS); the nightly-computed live
              counts from GET /platform-stats swap in after load. */}
          <div className="ul-stat">
            <span className="ul-stat-num">{fmt(stats.pages)}</span>
            <span className="ul-stat-label">{t("hero.stats.pages")}</span>
          </div>
          <div className="ul-stat">
            <span className="ul-stat-num">{fmt(stats.papers)}</span>
            <span className="ul-stat-label">{t("hero.stats.papers")}</span>
          </div>
          <div className="ul-stat">
            <span className="ul-stat-num">{fmt(stats.subjects)}</span>
            <span className="ul-stat-label">{t("hero.stats.subjects")}</span>
          </div>
        </div>
        <div className="ul-hero-stats-live">
          <span className="ul-live-dot" aria-hidden="true" />
          {/* Only claim "live, updated nightly" when live numbers actually
              arrived. Otherwise these are the baked census figures and the
              caption says so, with the date they were counted. The numbers are
              real either way — the claim about them must be too. */}
          {stats.isLive
            ? t("hero.stats.live")
            : t("hero.stats.census", { date: censusDate })}
        </div>
      </div>

      <div className="ul-hero-right">
        <div className="ul-hero-mock">
          <Link to="/sample-report" className="ul-hero-report-link" aria-label={t("hero.reportAria")}>
            <BrowserFrame domain="app.markszen.com" path="/sample-report" dark>
              <div className="ul-hero-report-scroll">
                <div className="ul-hero-report-label">
                  <span className="ul-report-label-title">
                    <span aria-hidden="true">📊</span> {t("hero.reportTitle")}
                  </span>
                  <span className="ul-report-label-sub">
                    {t("hero.reportSub")}
                  </span>
                </div>
                <Suspense fallback={null}>
                  <HeroReportPreview />
                </Suspense>
                <div className="ul-hero-report-cta">
                  <span>{t("hero.reportCta")}</span>
                </div>
              </div>
            </BrowserFrame>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default HeroSection;
